import React, { JSX } from 'react'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'
import Store from '../store/Store'
import Layout from '../components/ui/Layout'
import { Page } from '../components/ui/Page'
import { Body } from '../components/layout/Body'
import { Loading } from '../components/ui/Loading'
import FAQPage from '../pages/FAQPage'
import ImprintPage from '../pages/ImprintPage'
import PolicyPage from '../pages/PolicyPage'
import AccessPage from '../pages/AccessPage'
import ContactPage from '../pages/ContactPage'
import LoginPage from '../pages/LoginPage'
import OrdersPage from '../pages/OrdersPage'
import ProfilePage from '../pages/ProfilePage'
import {
	BookingPage1,
	BookingPage2,
	BookingPage3,
	BookingPage4,
	BookingPage5,
	BookingPage6,
} from '../pages/consumer/BookingPage'
import DashboardPageConsumer from '../pages/consumer/DashboardPage'
import OrderPageConsumer from '../pages/consumer/OrderPage'
import SearchPage from '../pages/consumer/SearchPage'
import UsersPage from '../pages/consumer/UsersPage'
import DashboardPageExtern from '../pages/extern/DashboardPage'
import OrderPageProvider from '../pages/provider/OrderPage'
import DashboardPageProvider from '../pages/provider/DashboardPage'
import Return from '../pages/consumer/Retour'
import MonetaryDashboard from '../pages/provider/MonetaryDashboard'
import TermsPage from '../pages/TermsPage'
import MapSearchPage from '../pages/extern/MapSearchPage'
import SearchOrders from '../pages/extern/SearchOrders'
import PaymentPage from '../pages/consumer/PaymentPage'
import MapEditPage from '../pages/provider/MapEditPage'
import FleetManagementPage from '../pages/provider/FleetManagementPage'
import DroneStationsPage from '../pages/provider/DroneStationsPage'
import TwoFactorPage from '../pages/TwoFactorPage'
import TwoFactorEnrollPage from '../pages/TwoFactorEnrollPage'
import EmergencyPage from '../pages/EmergencyPage'
import ProtectedRoute from './ProtectedRoute'
import DataManagementPage from '../pages/provider/DataManagementPage'

interface DashboardPages {
	EXTERN: JSX.Element
	CONSUMER: JSX.Element
	PROVIDER: JSX.Element
}

interface OrderPages {
	CONSUMER: JSX.Element
	PROVIDER: JSX.Element
	EXTERN: JSX.Element
}

const dashboard: DashboardPages = {
	EXTERN: <DashboardPageExtern />,
	CONSUMER: <DashboardPageConsumer />,
	PROVIDER: <DashboardPageProvider />,
}

const orderPage: OrderPages = {
	CONSUMER: <OrderPageConsumer />,
	PROVIDER: <OrderPageProvider />,
	EXTERN: <OrderPageProvider />,
}

function protectedRoute(children: JSX.Element) {
	return <ProtectedRoute>{children}</ProtectedRoute>
}

function RootRedirect() {
	const isLoggedIn = Store.use('isLoggedIn')
	const isLoaded = Store.use('isLoaded')

	if (!isLoaded) return <Loading spinner />

	return <Navigate to={isLoggedIn ? '/dashboard' : '/login'} replace />
}

function LoginRedirectIfAuthenticated() {
	const isLoggedIn = Store.use('isLoggedIn')
	const isLoaded = Store.use('isLoaded')

	if (!isLoaded) return <Loading spinner />

	if (isLoggedIn) {
		return <Navigate to="/dashboard" replace />
	}

	return <LoginPage />
}

function LoadingPage() {
	return (
		<div className="w-full h-full flex justify-center items-center">
			<Loading spinner />
		</div>
	)
}

function NotFoundPage() {
	return (
		<Page>
			<Body>
				<h1 className="text-2xl mt-16 mb-8 font-bold">
					404 – Seite nicht gefunden
				</h1>
				<p>Die angeforderte Seite existiert nicht.</p>
			</Body>
		</Page>
	)
}

export default function AppRouter() {
	const mode = Store.use('mode')

	return (
		<Routes>
			<Route path="/" element={<Layout />}>
				<Route index element={<RootRedirect />} />

				<Route path="loading" element={<LoadingPage />} />

				{/* PUBLIC */}
				<Route path="login" element={<LoginRedirectIfAuthenticated />} />
				<Route path="id-austria-login-successful" element={<LoginPage />} />
				<Route path="imprint" element={<ImprintPage />} />
				<Route path="policy" element={<PolicyPage />} />
				<Route path="contact" element={<ContactPage />} />
				<Route path="access" element={<AccessPage />} />
				<Route path="faq" element={<FAQPage />} />
				<Route path="2fa/enroll" element={<TwoFactorEnrollPage />} />

				{/* PROTECTED */}
				<Route path="2fa" element={protectedRoute(<TwoFactorPage />)} />
				<Route
					path="dashboard"
					element={protectedRoute(dashboard[mode as keyof DashboardPages])}
				/>
				<Route path="orders" element={protectedRoute(<OrdersPage />)} />
				<Route path="search" element={protectedRoute(<SearchPage />)} />
				<Route path="users" element={protectedRoute(<UsersPage />)} />
				<Route path="data-management" element={protectedRoute(<DataManagementPage />)} />
				<Route path="emergency" element={protectedRoute(<EmergencyPage />)} />
				<Route path="return" element={protectedRoute(<Return />)} />
				<Route path="mapSearch" element={protectedRoute(<MapSearchPage />)} />
				<Route path="searchOrders" element={protectedRoute(<SearchOrders />)} />
				<Route path="mapEdit" element={protectedRoute(<MapEditPage />)} />
				<Route
					path="flottenmanagement"
					element={protectedRoute(<FleetManagementPage section="drones" />)}
				/>
				<Route
					path="drohnenstationen"
					element={protectedRoute(<DroneStationsPage />)}
				/>
				<Route
					path="flaechen"
					element={protectedRoute(<FleetManagementPage section="areas" />)}
				/>
				<Route
					path="order"
					element={protectedRoute(orderPage[mode as keyof OrderPages])}
				/>
				<Route
					path="order/:orderId"
					element={protectedRoute(orderPage[mode as keyof OrderPages])}
				/>
				<Route path="payment" element={protectedRoute(<PaymentPage />)} />
				<Route path="money" element={protectedRoute(<MonetaryDashboard />)} />

				<Route path="booking" element={protectedRoute(<Outlet />)}>
					<Route index element={<BookingPage1 />} />
					<Route path="page_2" element={<BookingPage2 />} />
					<Route path="page_3" element={<BookingPage3 />} />
					<Route path="page_4" element={<BookingPage4 />} />
					<Route path="page_5" element={<BookingPage5 />} />
					<Route path="page_6" element={<BookingPage6 />} />
				</Route>

				<Route
					path="profile"
					element={protectedRoute(<ProfilePage id="profile-page" />)}
				/>
				<Route path="terms" element={protectedRoute(<TermsPage />)} />
				<Route path="*" element={<NotFoundPage />} />
			</Route>
		</Routes>
	)
}
